import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
    ScanningOutline,
    CheckCircleOutline,
    TruckOutline,
    EditSOutline,
    SearchOutline
} from "antd-mobile-icons";
import DspLayout from "./layouts/DspLayout";
import {DspPath} from "../../enums/DeliveryNavigatePathEnum";
import {getUserInfo} from "../../utils/auth";
import {useQuery} from "@apollo/client";
import {GET_ACCOUNT_BIND_DSP, GetAccountBindDspResponse} from "./graphql/queries/getAccountBindDsp";
import usePreventEdgeSwipe from "../../hooks/usePreventEdgeSwipe";
import useDspWarehouseStore from "./store/dspWarehouseStore";
import {DspValidationType} from "../../enums/DspValidationType";
import {
    DeliveredIcon,
    DeliveryFailedIcon,
    DriverViolationIcon,
    PackageSearchIcon,
    PodDocumentIcon,
    SortingIcon,
    TransferOutIcon
} from "../../assets/icons";
import DspWarehouseSelector from "./components/DspWarehouseSelector";
import {NativePermissions} from '../delivery/components/PhotoNativeBridge';
import {
    GET_DSP_HOME_PENDING_SUMMARY,
    GetDspHomePendingSummaryResponse,
    GetDspHomePendingSummaryVariables,
} from "./home/graphql/queries/getDspHomePendingSummary";
import {useCheckWarehouseIdInGrayConfig} from "../../graphql/queries/commonConfigQueries";

/** DSP 问题件工作台入口按仓灰度配置键（与后端 ConfigKey.DSP_ISSUE_PARCEL_WORKBENCH_GRAY_CONFIG 一致） */
const ISSUE_PARCEL_WORKBENCH_GRAY_CONFIG_KEY = 'dsp.issue_parcel_workbench.gray_config';

interface FunctionCardProps {
    icon: React.ReactNode;
    title: string;
    onClick: () => void;
    className?: string;
}

const FunctionCard: React.FC<FunctionCardProps> = ({icon, title, onClick, className = ""}) => {
    return (
        <div
            className={`bg-white p-6 border border-gray-100 flex flex-col items-center justify-center space-y-3 cursor-pointer transition-colors min-h-[120px] ${className}`}
            onClick={onClick}
        >
            <div className="text-5xl" style={{color: '#8B5CF6'}}>
                {icon}
            </div>
            <span className="text-sm font-medium text-center text-gray-700">
                {title}
            </span>
        </div>
    );
};

const DspHomePage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userInfo = getUserInfo();
    const userName = userInfo?.account.name || 'Kevin';
    const { data } = useQuery<GetAccountBindDspResponse>(GET_ACCOUNT_BIND_DSP);
    const setWarehouses = useDspWarehouseStore((state) => state.setWarehouses);
    const setDspId = useDspWarehouseStore((state) => state.setDspId);
    const selectedWarehouseId = useDspWarehouseStore((state) => state.selectedWarehouseId);

    const warehouseIdNumber = selectedWarehouseId ? Number(selectedWarehouseId) : null;
    const { data: pendingSummaryData } = useQuery<
        GetDspHomePendingSummaryResponse,
        GetDspHomePendingSummaryVariables
    >(GET_DSP_HOME_PENDING_SUMMARY, {
        variables: {
            warehouseId: warehouseIdNumber ?? 0,
        },
        skip: warehouseIdNumber == null,
        fetchPolicy: 'cache-and-network',
    });
    const pendingTotalCount = (pendingSummaryData?.getDspHomePendingSummary?.pendingIssueParcelCount ?? 0);

    // 问题件工作台入口按当前选中仓灰度：命中走新入口（问题件工作台），未命中走老入口（司机异常审核直进POD审核）
    const { data: workbenchGrayData, loading: workbenchGrayLoading } = useCheckWarehouseIdInGrayConfig(
        ISSUE_PARCEL_WORKBENCH_GRAY_CONFIG_KEY,
        warehouseIdNumber,
    );
    // loading 期间两个入口都不渲染，避免“先显老再跳新”闪烁
    const showNewWorkbenchEntry = !workbenchGrayLoading && (workbenchGrayData?.checkWarehouseIdInGrayConfig ?? false);
    const showOldPodReviewEntry = !workbenchGrayLoading && !(workbenchGrayData?.checkWarehouseIdInGrayConfig ?? false);

    React.useEffect(() => {
        if (data?.dsp?.warehouses) {
            setWarehouses(data.dsp.warehouses);
        }
        if (data?.dsp?.id != null) {
            setDspId(Number(data.dsp.id));
        }
    }, [data?.dsp?.warehouses, data?.dsp?.id, setWarehouses, setDspId]);


    usePreventEdgeSwipe(true, {
        targetPath: DspPath.DELIVERY_DSP_HOME,
        edgeWidth: 15
    });

    const requestCameraAndNavigate = async (path: string) => {
        try {
            const granted = await NativePermissions.request('camera');
            if (!granted) return;
        } catch (error) {
            console.error('Failed to request camera permission:', error);
            return;
        }
        navigate(path);
    };

    const handleReceivingSortingClick = () => {
        requestCameraAndNavigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_SCAN);
    };

    const handleTrackPackageClick = () => {
        const params = new URLSearchParams({
            successPath: DspPath.DELIVERY_DSP_TRACK_DETAIL
        });

        requestCameraAndNavigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
    };

    const handleDeliveredClick = () => {
        const params = new URLSearchParams({
            successPath: DspPath.DELIVERY_DSP_DELIVERED_POD,
            title: t('pages.dsp.home.delivered'),
            validationType: DspValidationType.SIGN_RECEIPT
        });

        requestCameraAndNavigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
    };

    const handleDeliveryAbnormalClick = () => {
        const params = new URLSearchParams({
            successPath: DspPath.DELIVERY_DSP_MARK_EXCEPTION,
            title: t('pages.dsp.home.deliveryFail'),
            validationType: DspValidationType.EXCEPTION_REPORT
        });

        requestCameraAndNavigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
    };

    const handlePodModifyClick = () => {
        const params = new URLSearchParams({
            successPath: DspPath.DELIVERY_DSP_DELIVERED_POD,
            title: t('pages.dsp.home.editPod'),
            validationType: DspValidationType.POD_MODIFY
        });

        requestCameraAndNavigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
    };

    const handleIssueParcelWorkbenchClick = () => {
        navigate(DspPath.DELIVERY_DSP_ISSUE_PARCEL_WORKBENCH);
    };

    // 老入口：直接进 POD 不合规审核页（不传 state，POD 页返回时自然回 DSP 首页）
    const handlePodReviewClick = () => {
        navigate(DspPath.DELIVERY_DSP_DRIVER_VIOLATION_REVIEW);
    };

    return (
        <DspLayout title={`${t('pages.dsp.home.hello')}, ${userName}`}>
            <div className="min-h-full bg-gradient-to-b from-purple-200 via-purple-100 to-white">
                <div className="px-4 py-2">
                    <div className="text-base font-medium">
                        {t('pages.dsp.home.currentSite')}：<DspWarehouseSelector />
                    </div>
                </div>
                {showNewWorkbenchEntry && (
                    <div className="px-4 pt-2">
                        <div
                            className="bg-white rounded-2xl shadow-sm p-4 inline-flex flex-col items-center justify-center cursor-pointer min-w-[96px]"
                            onClick={handleIssueParcelWorkbenchClick}
                        >
                            <DriverViolationIcon width={40} height={40}/>
                            <div className="text-xl font-semibold text-gray-800 mt-1">
                                {pendingTotalCount}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                                {t('pages.dsp.home.pendingIssueParcel', '待处理问题件')}
                            </div>
                        </div>
                    </div>
                )}
                <div className="p-4 space-y-6">
                {/* 库内功能区 */}
                <div className="space-y-4">
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <h2 className="text-base font-medium px-4 pt-4 pb-2">{t('pages.dsp.home.warehouseManagement')}</h2>
                        <div className="grid grid-cols-3">
                            <FunctionCard
                                icon={<SortingIcon className="w-full h-full" width='48' height='48'/>}
                                title={t('pages.dsp.home.sorting')}
                                onClick={handleReceivingSortingClick}
                                className="rounded-none border-0 rounded-tl-2xl rounded-bl-2xl"
                            />
                            <FunctionCard
                                icon={<PackageSearchIcon className="w-full h-full" width='48' height='48'/>}
                                title={t('pages.dsp.home.trackPackage')}
                                onClick={handleTrackPackageClick}
                                className="rounded-none border-0 rounded-tr-2xl rounded-br-2xl"
                            />
                        </div>
                    </div>
                </div>

                {/* 派送功能区 */}
                <div className="space-y-4">
                    <div className="space-y-4">
                        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <h2 className="text-base font-medium px-4 pt-4 pb-2">{t('pages.dsp.home.deliveryManagement')}</h2>
                            <div className="grid grid-cols-3">
                                <FunctionCard
                                    icon={<DeliveredIcon className="w-full h-full" width='48' height='48'/>}
                                    title={t('pages.dsp.home.delivered')}
                                    onClick={handleDeliveredClick}
                                    className="rounded-none border-0 rounded-tl-2xl rounded-bl-2xl"
                                />
                                <FunctionCard
                                    icon={<DeliveryFailedIcon className="w-full h-full" width='48' height='48'/>}
                                    title={t('pages.dsp.home.deliveryFail')}
                                    onClick={handleDeliveryAbnormalClick}
                                    className="rounded-none border-0"
                                />
                                <FunctionCard
                                    icon={<PodDocumentIcon className="w-full h-full" width='48' height='48'/>}
                                    title={t('pages.dsp.home.editPod')}
                                    onClick={handlePodModifyClick}
                                    className="rounded-none border-0 rounded-tr-2xl rounded-br-2xl"
                                />
                                {/* 老入口：灰度未命中时展示，点击直进 POD 不合规审核页 */}
                                {showOldPodReviewEntry && (
                                    <FunctionCard
                                        icon={<DriverViolationIcon className="w-full h-full" width='48' height='48'/>}
                                        title={t('pages.dsp.home.driverExceptionReview')}
                                        onClick={handlePodReviewClick}
                                        className="rounded-none border-0"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </DspLayout>
    );
};

export default DspHomePage;


